import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import {
  Building2, ShieldCheck, Users, Flower2, Award, HandHeart, PersonStanding,
  Eye, Target, Star, Gem, Stethoscope, Cpu, Lock, Sparkles, Quote,
} from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import {
  AMBASSADOR_IMG, DIFFERENTIATORS, CORE_VALUES, VMP,
  EXPERIENCE_STEPS, TRUST_POINTS,
} from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";

const ICONS = {
  Building2, ShieldCheck, Users, Flower2, Award, HandHeart, PersonStanding,
  Eye, Target, Star, Gem, Stethoscope, Cpu, Lock, Sparkles,
};

const STATS = [
  { v: "8", l: ["Core Wellness", "Pillars"] },
  { v: "Protocol-Led", l: ["Wellness", "Ecosystem"], text: true },
];

export default function About() {
  const { openBook } = useEnquiry();

  return (
    <main data-testid="about-page" className="bg-[#f7f2e8]">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px] md:pt-20 bg-[#050505]">
        <img
          src="/assets/site/hero-bokeh2.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-[#050505]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        <div className="absolute -top-40 -left-24 w-[55vw] h-[55vw] rounded-full bg-[#c9a14a]/10 blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-40 right-[-10%] w-[45vw] h-[45vw] rounded-full bg-[#3a2c12]/40 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <FadeIn className="py-14 md:py-20">
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.24em] uppercase text-[#c9a14a]">
              <Link to="/" className="text-[#f7f2e8]/60 hover:text-[#c9a14a] transition-colors">Home</Link>
              <ChevronRight size={11} className="text-[#f7f2e8]/40" />
              <span>About Us</span>
            </div>
            <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.06]">
              <span className="block text-[#f7f2e8]">Redefining Wellness.</span>
              <span className="block text-[#c9a14a]">Transforming Lives.</span>
            </h1>
            <p className="mt-6 text-[#f7f2e8]/80 text-base md:text-lg leading-relaxed max-w-lg">
              Carlton Wellness Center is India's premium protocol-led wellness network for
              prevention, recovery, slimming, anti-ageing, mental wellbeing and lifestyle health.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-12 gap-y-6" data-testid="about-stats">
              {STATS.map((s) => (
                <div key={s.v} className="min-w-[140px]">
                  <p className={`text-[#c9a14a] whitespace-nowrap ${s.text ? "font-serif-display text-2xl md:text-3xl leading-tight" : "font-numeric text-3xl md:text-4xl"}`}>{s.v}</p>
                  <p className="mt-2 text-[12px] text-[#f7f2e8]/80 leading-snug">{s.l[0]}<br />{s.l[1]}</p>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="relative flex items-end justify-center lg:h-[560px] self-end">
            <div
              aria-hidden="true"
              className="absolute bottom-[6%] left-1/2 -translate-x-1/2 w-[80%] aspect-square rounded-full bg-[#c9a14a]/[0.14] blur-[90px] pointer-events-none"
            />
            <img
              src={AMBASSADOR_IMG}
              alt="Mrunal Thakur — Brand Ambassador"
              data-testid="about-ambassador-image"
              className="relative max-h-[420px] lg:max-h-none lg:h-[96%] w-auto object-contain object-bottom drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
            />
          </FadeIn>
        </div>
      </section>

      {/* Our Story */}
      <section data-testid="about-story" className="bg-[#f7f2e8]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1fr_1.2fr_1fr] gap-12 items-center">
          <FadeIn>
            <p className="overline text-[#c9a14a]">Our Story</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl leading-[1.12] text-[#141414]">
              Beyond a Spa.<br />A Purpose Led<br />Wellness Center.
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-[#4a4a4a] text-base leading-relaxed">
              Born from the vision of Dr. Irfan Khan and the expertise of EBG Group, Carlton
              Wellness Center was created to fill a vital gap in India's wellness landscape.
              We bring together science, tradition, hospitality and technology to deliver
              measurable, long-term wellness outcomes.
            </p>
            <div className="mt-8 relative pl-6 border-l-2 border-[#c9a14a]">
              <Quote size={18} className="absolute -left-[11px] top-0 bg-[#f7f2e8] text-[#c9a14a]" strokeWidth={1.5} />
              <p className="font-serif-display text-2xl md:text-[26px] leading-[1.35] text-[#141414]">
                This is not a spa.
                <br />
                <span className="text-[#a8813a]">This is a system.</span>
                <br />
                A better way to live well.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} className="rounded-2xl overflow-hidden shadow-2xl">
            <img src="/assets/gallery/01.jpg" alt="Carlton Wellness Center reception" className="w-full aspect-[4/3] object-cover" />
          </FadeIn>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section data-testid="about-different" className="bg-[#f2ede3]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn>
            <p className="overline text-[#c9a14a]">What Makes Us Different</p>
          </FadeIn>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-y-12">
            {DIFFERENTIATORS.map((d, i) => {
              const Icon = ICONS[d.icon];
              return (
                <FadeIn key={d.title} delay={i * 0.08} className={`text-center px-6 ${i > 0 ? "lg:border-l lg:border-[#c9a14a]/20" : ""}`}>
                  <span className="mx-auto w-14 h-14 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#a8813a]">
                    <Icon size={24} strokeWidth={1.3} />
                  </span>
                  <p className="mt-5 font-semibold text-[#141414]">{d.title}</p>
                  <p className="mt-2 text-[13px] text-[#4a4a4a] leading-relaxed">{d.desc}</p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="about-values" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <FadeIn>
              <p className="overline text-[#c9a14a]">Our Core Values</p>
            </FadeIn>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-9">
              {CORE_VALUES.map((v, i) => {
                const Icon = ICONS[v.icon];
                return (
                  <FadeIn key={v.title} delay={i * 0.08} className="flex gap-4">
                    <span className="w-11 h-11 shrink-0 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#c9a14a]">
                      <Icon size={18} strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-semibold text-[#f7f2e8]">{v.title}</p>
                      <p className="mt-1.5 text-[13px] text-[#f7f2e8]/65 leading-relaxed">{v.desc}</p>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
          <FadeIn delay={0.15} className="relative rounded-2xl overflow-hidden">
            <img src="/assets/gallery/03.jpg" alt="Carlton Wellness Center lounge" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute bottom-5 right-5 left-5 sm:left-auto sm:max-w-sm bg-[#050505]/85 backdrop-blur border border-[#c9a14a]/30 p-6 flex gap-4">
              <Quote size={22} className="text-[#c9a14a] shrink-0" />
              <p className="font-serif-display text-lg text-[#f7f2e8] leading-snug">
                Wellness that stays with you.<br />A life well lived, every day.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Vision Mission Promise */}
      <section data-testid="about-vmp" className="bg-[#f2ede3]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-center text-[#141414]">
              Our Vision, Mission <span className="text-[#c9a14a]">& Promise</span>
            </h2>
          </FadeIn>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {VMP.map((v, i) => {
              const Icon = ICONS[v.icon];
              return (
                <FadeIn key={v.title} delay={i * 0.1} className="bg-[#f7f2e8] border border-[#c9a14a]/20 p-10 text-center">
                  <span className="mx-auto w-14 h-14 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#a8813a]">
                    <Icon size={24} strokeWidth={1.3} />
                  </span>
                  <p className="mt-6 font-semibold text-[#141414] text-lg">{v.title}</p>
                  <p className="mt-3 text-[13px] text-[#4a4a4a] leading-relaxed">{v.desc}</p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wellness Experience */}
      <section data-testid="about-experience" className="bg-[#f7f2e8]">
        <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-20 md:py-24">
          <FadeIn>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-center text-[#141414]">
              The Carlton <span className="text-[#c9a14a]">Wellness Experience</span>
            </h2>
          </FadeIn>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5">
            {EXPERIENCE_STEPS.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.07} className="group">
                <div className="rounded-xl overflow-hidden">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                </div>
                <p className="mt-4 text-[11px] font-bold tracking-[0.2em] text-[#c9a14a]">{s.n}</p>
                <p className="mt-1 font-semibold text-[#141414]">{s.title}</p>
                <p className="mt-1.5 text-[12px] text-[#4a4a4a] leading-relaxed">{s.desc}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Built on Trust */}
      <section data-testid="about-trust" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto grid lg:grid-cols-2 items-stretch">
          <FadeIn className="relative min-h-[280px]">
            <img src="/assets/gallery/04.jpg" alt="Carlton Wellness Center interior" className="absolute inset-0 w-full h-full object-cover" />
          </FadeIn>
          <FadeIn delay={0.1} className="px-5 md:px-12 py-16 md:py-24">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#f7f2e8]">
              Built on Trust. <span className="text-[#c9a14a]">Designed for You.</span>
            </h2>
            <p className="mt-6 text-[#f7f2e8]/70 text-base leading-relaxed max-w-lg">
              Every Carlton Wellness Center is thoughtfully designed to offer privacy, comfort
              and world-class amenities that help you relax, recover and rejuvenate — every
              time you visit.
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-6">
              {TRUST_POINTS.map((t) => {
                const Icon = ICONS[t.icon];
                return (
                  <div key={t.label} className="text-center">
                    <span className="mx-auto w-12 h-12 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#c9a14a]">
                      <Icon size={19} strokeWidth={1.4} />
                    </span>
                    <p className="mt-3 text-[11px] text-[#f7f2e8]/85 leading-snug">{t.label}</p>
                  </div>
                );
              })}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="about-cta" className="relative bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-32 -right-24 w-[45vw] h-[45vw] rounded-full bg-[#c9a14a]/[0.12] blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-40 -left-20 w-[40vw] h-[40vw] rounded-full bg-[#3a2c12]/40 blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1480px] mx-auto grid lg:grid-cols-[1.05fr_1fr] items-stretch">
          <FadeIn className="px-5 md:px-12 lg:px-16 py-16 md:py-24 flex flex-col justify-center">
            <p className="overline text-[#c9a14a]">The Next Step</p>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] text-[#f7f2e8]">
              Begin Your
              <br />
              <span className="text-[#c9a14a]">Wellness Journey.</span>
            </h2>
            <span className="mt-6 block w-14 h-px bg-[#c9a14a]/70" aria-hidden="true" />
            <p className="mt-6 text-[#f7f2e8]/75 text-base md:text-lg leading-relaxed max-w-lg">
              Your transformation starts with a single, unhurried conversation.
              A Carlton specialist will listen, understand your goals and design a
              protocol shaped around you.
            </p>
            <ul className="mt-8 space-y-3 text-[13px] text-[#f7f2e8]/75 max-w-md">
              {[
                "Complimentary first-time assessment",
                "Personalised, protocol-led plan",
                "Priority booking across every Carlton center",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#c9a14a] shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => openBook("assessment")} data-testid="about-book-assessment-button" className="btn-gold">
                Book Your Assessment <ArrowRight size={15} strokeWidth={2} />
              </button>
              <Link to="/memberships" data-testid="about-view-memberships-link" className="btn-outline-gold !text-[#f7f2e8] hover:!text-[#050505]">
                View Membership Plans
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.12} className="relative min-h-[420px] lg:min-h-[620px]">
            <img
              src="/assets/site/mrunal-cta.webp"
              alt="Mrunal Thakur — Brand Ambassador, Carlton Wellness Group"
              data-testid="about-cta-image"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent lg:bg-gradient-to-r lg:from-[#050505]/70 lg:via-transparent lg:to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent lg:bg-none" />
            <div className="absolute left-6 bottom-6 md:left-8 md:bottom-8 max-w-[220px] bg-[#050505]/70 backdrop-blur-sm border-l-2 border-[#c9a14a] pl-4 py-2">
              <p className="font-serif-display text-xl md:text-[22px] text-[#f7f2e8] leading-tight">Mrunal Thakur</p>
              <p className="mt-1 text-[10px] font-bold tracking-[0.22em] uppercase text-[#c9a14a] leading-snug">
                Brand Ambassador
                <br />
                Carlton Wellness Group
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
